import { BlogFont } from '@ViewModels';

/**
 * Style presets for Header and Footer customization only.
 * Blog-specific styles (BlogHome, BlogPost) are not included in presets
 * since presets are designed for shared components used across
 * blogs, surveys, and meetings.
 */
export interface StylePreset {
	id: string;
	name: string;
	description: string;
	colors: {
		header?: {
			textColor?: string;
			backgroundColor?: string;
			hoverColor?: string;
			dropdownTextColor?: string;
			dropdownBackgroundColor?: string;
		};
		footer?: {
			backgroundColor?: string;
			textColor?: string;
			linkColor?: string;
		};
	};
	fonts: {
		header?: BlogFont;
		footer?: BlogFont;
	};
}

const ROBOTO_FONT: BlogFont = {
	fontName: 'Roboto',
	fontUri: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
};

const OPEN_SANS_FONT: BlogFont = {
	fontName: 'Open Sans',
	fontUri: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700&display=swap',
};

const LATO_FONT: BlogFont = {
	fontName: 'Lato',
	fontUri: 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap',
};

const MONTSERRAT_FONT: BlogFont = {
	fontName: 'Montserrat',
	fontUri: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap',
};

const INTER_FONT: BlogFont = {
	fontName: 'Inter',
	fontUri: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap',
};

const POPPINS_FONT: BlogFont = {
	fontName: 'Poppins',
	fontUri: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap',
};

/**
 * Style presets provide curated design templates with custom color palettes.
 * These colors are intentionally hardcoded rather than using the theme system because:
 * 1. Each preset represents a distinct aesthetic (Professional, Modern, Bold, etc.)
 * 2. They serve as starting points that users can customize further
 * 3. They are independent of the application's brand colors
 */
export const STYLE_PRESETS: StylePreset[] = [
	{
		id: 'professional',
		name: 'Professional',
		description: 'Navy blue header with clean styling - perfect for a professional look',
		colors: {
			header: {
				textColor: '#ffffff',
				backgroundColor: '#1e3a5f',
				hoverColor: '#2c5282',
				dropdownTextColor: '#1a202c',
				dropdownBackgroundColor: '#ffffff',
			},
			footer: {
				backgroundColor: '#f7fafc',
				textColor: '#2d3748',
				linkColor: '#2b6cb0',
			},
		},
		fonts: {
			header: ROBOTO_FONT,
			footer: ROBOTO_FONT,
		},
	},
	{
		id: 'modern',
		name: 'Modern',
		description: 'Bold colors, sans-serif fonts, high contrast - a contemporary look',
		colors: {
			header: {
				textColor: '#ffffff',
				backgroundColor: '#000000',
				hoverColor: '#2d3748',
				dropdownTextColor: '#000000',
				dropdownBackgroundColor: '#f7fafc',
			},
			footer: {
				backgroundColor: '#1a202c',
				textColor: '#e2e8f0',
				linkColor: '#63b3ed',
			},
		},
		fonts: {
			header: INTER_FONT,
			footer: INTER_FONT,
		},
	},
	{
		id: 'bold',
		name: 'Bold',
		description: 'Bright accent colors, strong typography - make a statement',
		colors: {
			header: {
				textColor: '#ffffff',
				backgroundColor: '#d53f8c',
				hoverColor: '#b83280',
				dropdownTextColor: '#2d3748',
				dropdownBackgroundColor: '#ffffff',
			},
			footer: {
				backgroundColor: '#2d3748',
				textColor: '#ffffff',
				linkColor: '#f687b3',
			},
		},
		fonts: {
			header: POPPINS_FONT,
			footer: POPPINS_FONT,
		},
	},
	{
		id: 'minimal',
		name: 'Minimal',
		description: 'Subtle grays, simple fonts, lots of whitespace - clean and understated',
		colors: {
			header: {
				textColor: '#4a5568',
				backgroundColor: '#ffffff',
				hoverColor: '#edf2f7',
				dropdownTextColor: '#2d3748',
				dropdownBackgroundColor: '#ffffff',
			},
			footer: {
				backgroundColor: '#edf2f7',
				textColor: '#4a5568',
				linkColor: '#718096',
			},
		},
		fonts: {
			header: LATO_FONT,
			footer: LATO_FONT,
		},
	},
	{
		id: 'creative',
		name: 'Creative',
		description: 'Unique color combinations, interesting fonts - stand out from the crowd',
		colors: {
			header: {
				textColor: '#ffffff',
				backgroundColor: '#2c7a7b',
				hoverColor: '#285e61',
				dropdownTextColor: '#1a202c',
				dropdownBackgroundColor: '#e6fffa',
			},
			footer: {
				backgroundColor: '#f0fff4',
				textColor: '#22543d',
				linkColor: '#2c7a7b',
			},
		},
		fonts: {
			header: MONTSERRAT_FONT,
			footer: OPEN_SANS_FONT,
		},
	},
];

export const CUSTOM_PRESET: StylePreset = {
	id: 'custom',
	name: 'Custom',
	description: 'Your custom settings',
	colors: {},
	fonts: {},
};

export function getPresetById(id: string): StylePreset | undefined {
	if (id === 'custom') {
		return CUSTOM_PRESET;
	}
	return STYLE_PRESETS.find(preset => preset.id === id);
}

export function getAllPresets(): StylePreset[] {
	return [...STYLE_PRESETS, CUSTOM_PRESET];
}
